/**
 * @file EmailGatePage.jsx — /verificar-email
 */
import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Mail, AlertCircle } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { authService } from "../services/auth";
import { EMAIL_GATE_STRINGS } from "../constants/strings";
import AuthShell from "../components/AuthShell";
import { Button, Input, Spinner, toast } from "../ui";

export default function EmailGatePage() {
  const { user, isLoading, isAuthenticated, handleAuthSuccess } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(null);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (isLoading) {
    return (
      <AuthShell>
        <div className="auth-status">
          <Spinner size={28} />
        </div>
      </AuthShell>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user.isEmailVerified) return <Navigate to="/mi-salud" replace />;

  const currentStep = step || (user.email ? "code" : "email");

  const handleSendCode = async () => {
    const trimmed = email.trim();
    if (!/\S+@\S+\.\S+/.test(trimmed)) {
      setError(EMAIL_GATE_STRINGS.INVALID_EMAIL);
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await authService.addEmail(trimmed);
      setStep("code");
      toast.success(`${EMAIL_GATE_STRINGS.CODE_SENT} ${trimmed}`);
    } catch (err) {
      setError(err.response?.data?.message || "Error al enviar el código");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    const targetEmail = email.trim() || user.email;
    if (!targetEmail) return;
    setSubmitting(true);
    try {
      await authService.addEmail(targetEmail);
      toast.success(`${EMAIL_GATE_STRINGS.CODE_SENT} ${targetEmail}`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al reenviar el código");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!/^\d{6}$/.test(code)) {
      setError(EMAIL_GATE_STRINGS.INVALID_CODE);
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const res = await authService.verifyEmailCode(code);
      handleAuthSuccess(res.data);
      toast.success(EMAIL_GATE_STRINGS.SUCCESS);
      navigate("/mi-salud", { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || "Código inválido o expirado");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title={EMAIL_GATE_STRINGS.TITLE}
      subtitle={
        currentStep === "email"
          ? EMAIL_GATE_STRINGS.SUBTITLE
          : EMAIL_GATE_STRINGS.SUBTITLE_HAS_EMAIL
      }
    >
      <div className="auth-status" style={{ paddingBottom: 0 }}>
        <Mail size={36} className="auth-status-ok" style={{ color: "var(--primary)" }} />
      </div>
      {error && (
        <div className="auth-alert auth-alert-error">
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}
      {currentStep === "email" ? (
        <div className="auth-form">
          <Input
            autoFocus
            label={EMAIL_GATE_STRINGS.EMAIL_LABEL}
            placeholder={EMAIL_GATE_STRINGS.EMAIL_PLACEHOLDER}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSendCode()}
          />
          <Button
            size="lg"
            fullWidth
            onClick={handleSendCode}
            loading={submitting}
            disabled={!email.trim()}
          >
            {EMAIL_GATE_STRINGS.SEND_CODE}
          </Button>
        </div>
      ) : (
        <div className="auth-form">
          <p style={{ fontSize: 13, color: "var(--ink-3)", textAlign: "center", margin: 0 }}>
            {EMAIL_GATE_STRINGS.CODE_SENT} <strong>{email.trim() || user.email}</strong>
          </p>
          <input
            className="auth-code-input"
            inputMode="numeric"
            autoFocus
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            onKeyDown={(e) => e.key === "Enter" && handleVerifyCode()}
            aria-label={EMAIL_GATE_STRINGS.CODE_LABEL}
            placeholder={EMAIL_GATE_STRINGS.CODE_PLACEHOLDER}
          />
          <Button
            size="lg"
            fullWidth
            onClick={handleVerifyCode}
            loading={submitting}
            disabled={code.length !== 6}
          >
            {EMAIL_GATE_STRINGS.VERIFY}
          </Button>
          <Button variant="ghost" fullWidth onClick={handleResendCode} disabled={submitting}>
            {EMAIL_GATE_STRINGS.RESEND_CODE}
          </Button>
        </div>
      )}
    </AuthShell>
  );
}
